import { View } from "@-/com.npmjs.react-native";
import { publishOnboardingMapFrame } from "@-/dev.improve.haniverse.routes.nav.app.onboarding.handoff";
import { useCallback, useRef } from "react";
import { Path, Svg } from "@-/com.npmjs.react-native-svg";
import type { ChinaMapRegionId } from "@-/dev.improve.haniverse.data.china-map";
import outlines from "#map-skeleton-outlines.json" with { type: "json" };
import { CHINA_MAP_FIT_PADDING } from "#china-map-camera";
import { useChinaMapColors } from "#use-china-map-colors";

/**
 * Bundled silhouettes merged and simplified offline from the committed map outlines (2 map
 * units of tolerance). No place data, downloaded assets, or interactive renderer are needed.
 *
 * The silhouette is framed exactly as the canvas first frames the whole map (fitted, with
 * `CHINA_MAP_FIT_PADDING`, between the toolbar and the sheet), so the map draws directly over it.
 * The insets are the canvas's own, measured from its top and bottom edges.
 */
export function ChinaMapLoadingSkeleton({
  bottomInset,
  regionId,
  topInset,
}: {
  bottomInset: number;
  regionId: ChinaMapRegionId;
  topInset: number;
}) {
  const outline = outlines[regionId];
  const colors = useChinaMapColors();
  const viewRef = useRef<View>(null);
  // The onboarding's globe lands China on this very silhouette (`onboarding.handoff`), so the
  // mainland's says where it is framed, as the SVG's `xMidYMid meet` fits it into this box.
  const publishFrame = useCallback(() => {
    if (regionId !== "mainland") {
      return;
    }
    viewRef.current?.measureInWindow((x, y, width, height) => {
      if (width <= 0 || height <= 0) {
        return;
      }
      const scale = Math.min(width / outline.width, height / outline.height);
      publishOnboardingMapFrame({
        scale,
        x: x + (width - outline.width * scale) / 2,
        y: y + (height - outline.height * scale) / 2,
      });
    });
  }, [outline.height, outline.width, regionId]);
  return (
    <View
      accessibilityElementsHidden
      accessible={false}
      className="absolute"
      importantForAccessibility="no-hide-descendants"
      onLayout={publishFrame}
      pointerEvents="none"
      ref={viewRef}
      style={{
        bottom: bottomInset + CHINA_MAP_FIT_PADDING,
        left: CHINA_MAP_FIT_PADDING,
        right: CHINA_MAP_FIT_PADDING,
        top: topInset + CHINA_MAP_FIT_PADDING,
      }}
      testID="china-map-loading-skeleton"
    >
      {/* No `accessible` here: the view above already hides its contents, and on web
          react-native-svg would hand the prop to the <svg> element as an invalid attribute. */}
      <Svg
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${outline.width} ${outline.height}`}
        width="100%"
      >
        <Path
          d={outline.path}
          fill={colors.calloutLabel}
          fillOpacity={0.12}
          fillRule="evenodd"
          stroke={colors.calloutLabel}
          strokeLinejoin="round"
          strokeOpacity={0.16}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      </Svg>
    </View>
  );
}
